<template>
  <AppDialogContent
    v-model="dialogValue"
    :loading="formSubmitted"
    :persistent="formSubmitted"
    custom-header
    @submit-form="submit"
  >
    <template #header>
      <div class="d-flex justify-space-between py-4">
        <div v-if="!!responseType" style="align-self: center">
          <div class="text-h5 font-weight-bold">
            {{ responseTexts.headline }}
          </div>
        </div>
        <div v-else style="align-self: center">
          <div class="text-h5 font-weight-bold mb-2">
            {{
              $t(
                "events:entryAction.approveCandidacy.heading",
                "Approve candidacy confirmation"
              )
            }}
          </div>
          <p>
            {{
              $t(
                "events:entryAction.approveCandidacy.explanation.text",
                "You are going to approve the following FEI candidacy (entry)"
              )
            }}
          </p>
        </div>
        <div class="float-end">
          <v-btn-icon
            icon
            variant="text"
            :disabled="formSubmitted"
            class="mx-0"
            data-test="closeDialog"
            :aria-label="$t('common:closeButton.alt')"
            @click="dialogValue = false"
          >
            <v-icon>$iconTimesCircle</v-icon>
          </v-btn-icon>
        </div>
      </div>
    </template>

    <template #default>
      <div v-if="!!responseType">
        <EntryFeiResponse
          :response-type="responseType"
          :mutation-errors="mutationErrors"
        >
          <template #default>
            {{ responseTexts.main }}
          </template>
          <template #reason>
            <div v-if="responseTexts.reason">
              {{ responseTexts.reason }}<br />
            </div>
          </template>
          <template #additional-message>
            {{ responseTexts.additional }}
          </template>
        </EntryFeiResponse>
      </div>
      <div v-else-if="entryLoading">
        <v-progress-linear indeterminate />
      </div>
      <div v-else-if="entry">
        <AppShowEventDetails v-if="event" :event="event" show-logo />

        <br />

        <AppEntryRegistrationOverview v-bind="{ registration, competition }">
          <template #competitionCodeheader>
            {{
              $t(
                "events:entryAction.approveCandidacy.competitionCode.label",
                "Entry"
              )
            }}
          </template>
          <template #athleteNamesHeader>
            {{
              $t(
                "events:entryAction.approveCandidacy.athleteNames.label",
                "Athlete"
              )
            }}
          </template>
          <template #horseNamesHeader>
            {{
              $t(
                "events:entryAction.approveCandidacy.horseNames.label",
                "Horse"
              )
            }}
          </template>
        </AppEntryRegistrationOverview>

        <br />

        <v-radio-group
          v-if="entry.canBeApprovedUnderHomeQuota == true"
          v-model="useHomeQuota"
        >
          <template #label>
            <span style="font-size: 16px"
              ><strong>{{
                $t(
                  "events:entryAction.approveCandidacy.selectQuota.heading",
                  "Select quota"
                )
              }}</strong></span
            >
          </template>
          <v-radio
            :value="true"
            :label="
              $t(
                'events:entryAction.approveCandidacy.selectQuota.homeNFChoice.label',
                'Enter as Home NF entry'
              )
            "
          />
          <v-radio
            :value="false"
            :label="
              $t(
                'events:entryAction.approveCandidacy.selectQuota.OCquotaChoice.label',
                'Submit entry to waiting list (under OC quota)'
              )
            "
          />
        </v-radio-group>

        <br />

        <div class="mb-2">
          <b>{{
            $t(
              "events:entryAction.approveCandidacy.alertNote.heading",
              "Please note:"
            )
          }}</b>
        </div>

        <AppAlertNotice center>
          {{
            $t(
              "events:entryAction.approveCandidacy.alertNoteFei.text",
              "Entry will be marked as approved by NF and will appear in waiting status or if submitted within Home NF quota, will be accepted automatically. Athlete will get an email notification that candidacy was approved by NF."
            )
          }}
        </AppAlertNotice>
      </div>
    </template>

    <template #submit>
      {{
        $t(
          "events:entryAction.approveCandidacy.confirmApproval.button",
          "Confirm approval"
        )
      }}
    </template>

    <template v-if="!!responseType" #actions>
      <v-row dense style="justify-content: flex-end">
        <div class="mb-1 text-center text-md-right">
          <v-btn
            rounded
            variant="outlined"
            min-width="180px"
            data-test="dialogClose"
            @click="dialogValue = false"
          >
            {{ responseTexts.okBtn }}
          </v-btn>
        </div>
      </v-row>
    </template>
  </AppDialogContent>
</template>
<script setup>
import { ref, computed } from "vue";
import gql from "fraql";
import { apolloClient } from "@/plugins/apollo";
import { useEntryFeiActionsInjections } from "./compositions";
import AppDialogContent from "@elements/AppDialogContent.vue";
import AppShowEventDetails from "@AppShows/event/AppShowEventDetails.vue";

import AppAlertNotice from "@elements/AppAlertNotice/index.vue";
import EntryFeiResponse from "./EntryFeiResponse.vue";
import AppEntryRegistrationOverview from "@elements/AppEntryRegistrationOverview.vue";

import { injectSnackbarError } from "@/composables/injections";
import { useTranslation } from "i18next-vue";

defineOptions({
  name: "EntryFeiApproveCandidacy"
});

const { fragments, refetchQueries } = useEntryFeiActionsInjections();

const { snackbarServerError } = injectSnackbarError();
const { t } = useTranslation();

const modal = ref(false);
const useHomeQuota = ref(true);
const formSubmitted = ref(false);
const responseType = ref(null);
const mutationErrors = ref([]);

const entryLoading = ref(false);
const entry = ref(null);

const registration = computed(() => {
  return entry.value?.registration;
});
const event = computed(() => {
  return entry.value?.registration?.event;
});
const competition = computed(() => {
  return entry.value?.competition;
});

async function openFeiActionForm({ entry: e }) {
  entryLoading.value = true;
  dialogValue.value = true;
  try {
    const { data } = await apolloClient.query({
      query: gql`
        query EntryFeiApproveCandidacyFetch($id: ID!) {
          entry(id: $id) {
            id
            connectedWithFei
            canBeApprovedUnderHomeQuota
            allowedMutations
            registration {
              id
              eventId
              event {
                id
                ${AppShowEventDetails.fragments.event}
              }
              athletes {
                id
                name
                ${AppEntryRegistrationOverview.fragments.personFields}
              }
              horses {
                id
                name
                ${AppEntryRegistrationOverview.fragments.horseFields}
              }
            }
            competition {
              id
              ${AppEntryRegistrationOverview.fragments.competitionFields}
            }
          }
        }
      `,
      variables: {
        id: e.id
      },
      fetchPolicy: "network-only"
    });
    entry.value = data.entry;
  } catch (error) {
    snackbarServerError(error, { level: "error" });
  } finally {
    entryLoading.value = false;
  }
}

const dialogValue = computed({
  get() {
    return modal.value;
  },
  set(v) {
    if (v == true && !modal.value) {
      responseType.value = null;
      mutationErrors.value = [];
      useHomeQuota.value = true;
    }
    modal.value = v;
  }
});

const responseTexts = computed(() => {
  switch (responseType.value) {
    case "success":
      return {
        headline: t(
          "events:entryAction.approveCandidacy.success.heading",
          "Candidacy approval"
        ),
        main: t(
          "events:entryAction.approveCandidacy.success.mainMessage.text",
          "Action is successful and entry put to waiting status"
        ),
        additional: t(
          "events:entryAction.approveCandidacy.success.additionalMessage.text",
          "Please note, athlete will get a notification of action."
        ),
        okBtn: t("events:entryAction.approveCandidacy.success.ok.button", "OK")
      };
    case "error":
      return {
        headline: t(
          "events:entryAction.approveCandidacy.failed.heading",
          "Candidacy approval"
        ),
        main: t(
          "events:entryAction.approveCandidacy.failed.mainMessage.text",
          "Action failed: candidacy cannot be approved"
        ),
        reason: t(
          "events:entryAction.approveCandidacy.failed.reason.text",
          "The FEI entry system reported the following reason:"
        ),
        additional: t(
          "events:entryAction.approveCandidacy.failed.additionalMessage.text",
          "Please contact technical support, if you need help on this matter"
        ),
        okBtn: t("events:entryAction.approveCandidacy.failed.ok.button", "OK")
      };
    default:
      return null;
  }
});

async function submit() {
  formSubmitted.value = true;

  try {
    const { errors } = await apolloClient.mutate({
      mutation: gql`
        mutation entryApproveCandidacy($entryId: ID, $useHomeQuota: Boolean) {
          entryApproveCandidacy(
            input: { entryId: $entryId, useHomeQuota: $useHomeQuota }
          ) {
            entry {
              id
              status
              resolution
              connectedWithFei
              canBeApprovedUnderHomeQuota
              allowedMutations
            }
          }
        }
      `,
      variables: {
        entryId: entry.value.id,
        useHomeQuota: entry.value.canBeApprovedUnderHomeQuota
          ? useHomeQuota.value
          : null
      }
    });

    if (errors?.length) {
      responseType.value = "error";
      mutationErrors.value = errors;
    } else {
      responseType.value = "success";
    }
  } catch (error) {
    responseType.value = "error";
    if (error?.graphQLErrors) {
      mutationErrors.value = error.graphQLErrors;
    }
  } finally {
    formSubmitted.value = false;
  }
}

defineExpose({ openFeiActionForm });
</script>
